
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export const useDeepDive = () => {
  const [scriptureReference, setScriptureReference] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!scriptureReference.trim()) {
      toast({
        title: "Please enter a Bible reference",
        description: "Enter a verse, range, or chapter to get commentary.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setCommentary(null);

    try {
      const { data, error } = await supabase.functions.invoke("deep-dive", {
        body: {
          verseReference: scriptureReference.trim(),
        },
      });

      if (error) throw error;

      if (data && data.commentary) {
        setCommentary(data.commentary);
      } else {
        throw new Error("No commentary received");
      }
    } catch (error) {
      console.error("Error fetching commentary:", error);
      toast({
        title: "Error fetching commentary",
        description: "Please try again later or use a different reference.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveCommentary = async () => {
    if (!commentary || !scriptureReference) {
      toast({
        title: "No Commentary",
        description: "There is no commentary to save.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to save commentary.",
          variant: "destructive",
        });
        return;
      }

      // Create a plain JSON object for the content
      const contentObj = {
        reference: scriptureReference,
        commentary,
        createdAt: new Date().toISOString()
      };

      const { error } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'deep_dive',
          title: `Deep Dive: ${scriptureReference}`,
          content: contentObj,
          user_id: session.session.user.id
        });

      if (error) {
        throw error;
      }

      toast({
        title: "Commentary Saved",
        description: "Your commentary has been saved to your dashboard.",
      });
    } catch (error) {
      console.error('Error saving commentary:', error);
      toast({
        title: "Error",
        description: "Failed to save commentary. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    scriptureReference,
    setScriptureReference,
    isLoading,
    isSaving,
    commentary,
    handleSubmit,
    saveCommentary,
  };
};
